/**
 * childAgentReadiness — is a sub-agent this graph dispatches actually runnable?
 *
 * A dispatch node runs ANOTHER agent, and that agent dying for want of a model
 * or a key kills the parent's run too. So the parent's Trigger check follows
 * the dispatch edges rather than stopping at its own nodes.
 *
 * The child's readiness is computed from the CHILD's own row — its graph, its
 * per-node overrides, its OWN env snapshot. A node's key is snapshotted onto
 * the agent that runs it and the runtime reads that BEFORE the project
 * credential, so judging a child by the parent's credential map marks every
 * child keyless whose key lives on itself, while the child's own page shows
 * those same nodes green. Two surfaces, same question, opposite answers.
 */
import { unreadyNodes, unsetModelNodes } from './nodeModel';

/** The distinct child slugs a graph dispatches, in stable order. */
export function dispatchedSlugs(graph) {
  const cfgs = graph?.nodeConfigs;
  if (!cfgs || typeof cfgs !== 'object') return [];
  const out = [];
  for (const id of Object.keys(cfgs)) {
    const slug = cfgs[id]?.dispatchesWorkflow;
    if (typeof slug === 'string' && slug && !out.includes(slug)) out.push(slug);
  }
  return out;
}

/** `{ENV_NAME: lastFour|true}` from a GET /workflows/:uuid/env response. */
export function envKeyMap(envResponse) {
  if (!envResponse) return null;
  const map = {};
  for (const e of (envResponse.entries || [])) {
    if (e && e.key) map[e.key] = e.lastFour || true;
  }
  for (const k of (envResponse.keys || [])) if (!map[k]) map[k] = true;
  return map;
}

/**
 * Every UNSET model node reachable from this graph — its own plus, following
 * the dispatch edges depth-first with a cycle guard, every deployed child
 * agent's (same project only; a child is deployed under its exact slug by the
 * deploy cascade). Children are read via `fetchChild(slug)` → the child's own
 * row (graph + nodeConfigOverrides + marketplace) — their model state lives on
 * THEIR rows, never the parent's. A child that can't be read contributes
 * nothing (never blame what you can't see) but is named in `unreadable` so the
 * caller can say so instead of silently under-counting.
 *
 * @returns {Promise<{selfIds: string[],
 *          children: Array<{slug: string, uuid: string, ids: string[]}>,
 *          unreadable: string[]}>}
 */
export async function collectUnsetModelTargets({
  graph, overrides, modelBlocks, excludeId = null, fetchChild,
}) {
  const selfIds = unsetModelNodes({ graph, overrides, modelBlocks, excludeId });
  const children = [];
  const unreadable = [];
  const visited = new Set();
  const walk = async (g) => {
    for (const slug of dispatchedSlugs(g)) {
      if (visited.has(slug)) continue;
      visited.add(slug);
      let row = null;
      try { row = await fetchChild(slug); } catch { row = null; }
      if (!row || row.notInstalled === true || !row.uuid || !row.graph) {
        unreadable.push(slug);
        continue;
      }
      const ids = unsetModelNodes({
        graph: row.graph,
        overrides: row.nodeConfigOverrides || {},
        modelBlocks: Array.isArray(row.marketplace?.models) ? row.marketplace.models : null,
      });
      if (ids.length) children.push({ slug, uuid: row.uuid, ids });
      await walk(row.graph);
    }
  };
  await walk(graph);
  return { selfIds, children, unreadable };
}

/**
 * Is this child runnable?
 *
 * @param {object} childRow   the child's workflow row (graph, nodeConfigOverrides, marketplace)
 * @param {object} childEnv   the child's own env-key map (see envKeyMap)
 * @param {object} projectSecrets shared project credentials
 * @param {string} projectVendor  the project's default vendor
 * @returns {boolean|null} null when the child can't be judged (not deployed,
 *          unreadable) — an unknown child is never blamed.
 */
export function childIsReady(childRow, childEnv, projectSecrets, projectVendor) {
  const graph = childRow?.graph;
  if (!graph || !Array.isArray(graph.nodes) || graph.nodes.length === 0) return null;
  return unreadyNodes({
    graph,
    overrides: childRow.nodeConfigOverrides || {},
    agentEnvKeys: childEnv,
    projectSecrets,
    projectVendor,
    modelBlocks: Array.isArray(childRow.marketplace?.models) ? childRow.marketplace.models : null,
  }).length === 0;
}
